import React from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import {
  Pressable,
  type StyleProp,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';
import Animated from 'react-native-reanimated';
import { BodyText, Icon, RotatingIcon, theme } from '@sg/garnish';

import { ICON_SIZE, useOrderPlaybackCta } from '../utils';

export const OrderPlaybackCta = (props: OrderPlaybackCtaProps) => {
  const {
    orderPlaybackCta,
    isPostOrder,
    isLoading,
    onPress,
    fadeIn,
    animationDuration,
  } = props;

  const isDisabled = isLoading || isPostOrder;

  const { formatMessage } = useIntl();
  const accessibilityLabel = formatMessage(messages.submitOrder);

  const { ctaRef, handleOnLayout, backgroundAnimatedStyle } =
    useOrderPlaybackCta({ fadeIn, animationDuration });

  return (
    <Pressable
      ref={ctaRef}
      style={[styles.cta, isDisabled && styles.ctaDisabled]}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      onPress={onPress}
      disabled={isDisabled}
      onLayout={handleOnLayout}
    >
      {isLoading ? <LoadingState /> : null}

      {isPostOrder ? <PostOrderState /> : null}

      {isDisabled ? null : (
        <ReadyState
          orderPlaybackCta={orderPlaybackCta}
          backgroundAnimatedStyle={backgroundAnimatedStyle}
        />
      )}
    </Pressable>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const LoadingState = () => {
  return (
    <>
      <BodyText sizeMatch={['18']} style={styles.ctaText}>
        <FormattedMessage {...messages.submittingOrder} />
      </BodyText>

      <RotatingIcon
        duration={600}
        delay={400}
        color="white"
        name="IconOrderPlaybackHexagon"
        width="24"
        height="24"
      />
    </>
  );
};

const PostOrderState = () => {
  return (
    <>
      <BodyText sizeMatch={['18']} style={styles.ctaText}>
        <FormattedMessage {...messages.orderConfirmed} />
      </BodyText>

      <Icon
        name="IconBadgeCheck"
        color="white"
        width={ICON_SIZE}
        height={ICON_SIZE}
      />
    </>
  );
};

const ReadyState = (props: OrderPlaybackReadyState) => {
  const { orderPlaybackCta, backgroundAnimatedStyle } = props;

  return (
    <>
      <View style={styles.animatedOverlayWrapper}>
        <Animated.View
          style={[styles.animatedOverlay, backgroundAnimatedStyle]}
        />
      </View>

      <BodyText sizeMatch={['18']} style={styles.ctaText}>
        {orderPlaybackCta}
      </BodyText>

      <View style={styles.iconContainer}>
        <Icon
          name="IconArrowRight"
          width="24"
          height="24"
          style={styles.icon}
          color={theme.colors.WHITE}
        />
      </View>
    </>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  submittingOrder: {
    defaultMessage: 'Submitting order',
    description: 'Bag | Order playback | Submitting order',
  },
  orderConfirmed: {
    defaultMessage: 'Order confirmed',
    description: 'Bag | Order playback | Order confirmed',
  },
  submitOrder: {
    defaultMessage: 'Submit order',
    description: 'Bag | Order playback | Submit order',
  },
});

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  cta: {
    minHeight: 64,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing['2'],
    backgroundColor: theme.colors.SPINACH,
    borderWidth: 1,
    borderColor: theme.colors.SPINACH,
    borderRadius: theme.radius['3xLarge'],
    paddingHorizontal: theme.spacing['8'],
    marginTop: -theme.spacing['4'],
    position: 'relative',
  },
  ctaDisabled: {
    backgroundColor: theme.colors.KALE,
  },
  ctaText: {
    color: theme.colors.WHITE,
  },
  animatedOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '0%',
    height: '100%',
    backgroundColor: theme.colors.KALE,
  },
  animatedOverlayWrapper: {
    ...StyleSheet.absoluteFillObject,
    overflow: 'hidden',
    borderRadius: theme.radius['3xLarge'],
  },
  iconContainer: {
    width: ICON_SIZE,
    height: ICON_SIZE,
  },
  icon: {
    position: 'absolute',
    top: 0,
    left: 0,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type OrderPlaybackCtaProps = {
  orderPlaybackCta: string;
  isPostOrder: boolean;
  isLoading: boolean;
  onPress: () => void;
  fadeIn: number;
  animationDuration: number;
};

type OrderPlaybackReadyState = {
  orderPlaybackCta: string;
  backgroundAnimatedStyle: StyleProp<ViewStyle>;
};
